// The top search's Skills leg (desktop bar and phone overlay): the workspace's skills, read
// at most once a minute and matched locally; scoped to Skills ("in Skills") it also asks the
// marketplaces. Opening a hit goes to Agents → Skills on it.
import { goto } from "$app/navigation";
import { client } from "$lib/state/app.svelte.js";
import { agentsPaneState } from "$lib/state/app.svelte.js";
import { skillsNav } from "$lib/state/skills-nav.svelte.js";
import { reportUiFailure } from "$lib/report-failure.js";
import { reportSearchLegFailure } from "./global-search.js";
import type { Skill } from "$lib/plugins/agents/types.js";
import {
  skillSearchHits,
  workspaceSkillHits,
  type SkillSearchHit,
} from "$lib/components/panes/skills-library-view-model.js";

const SKILLS_TTL_MS = 60_000;
let held: { workspaceId: string; at: number; skills?: Skill[]; pending?: Promise<Skill[]> } | null =
  null;

export function primeSkillsCache(workspaceId: string, skills: Skill[]): void {
  held = { workspaceId, at: Date.now(), skills };
}

function fresh(workspaceId: string): boolean {
  return !!held && held.workspaceId === workspaceId && Date.now() - held.at < SKILLS_TTL_MS;
}

function workspaceSkills(workspaceId: string): Promise<Skill[]> {
  if (fresh(workspaceId) && held?.skills) return Promise.resolve(held.skills);
  if (held?.workspaceId === workspaceId && held.pending) return held.pending;
  const pending = Promise.resolve()
    .then(() => client.listSkills(workspaceId))
    .then((r) => {
      held = { workspaceId, at: Date.now(), skills: r.skills };
      return r.skills;
    });
  held = { workspaceId, at: held?.workspaceId === workspaceId ? held.at : 0, skills: held?.workspaceId === workspaceId ? held.skills : undefined, pending }; // prettier-ignore
  pending.catch((err: unknown) => {
    if (held?.pending === pending) held = { ...held, pending: undefined };
    reportSearchLegFailure("skills", err, workspaceId);
  });
  return pending;
}

export interface SkillsLegResult {
  hits: SkillSearchHit[];
  // The marketplaces did not answer; the workspace's own hits still show.
  marketplaceFailed: boolean;
}

// The whole-workspace bar uses only a list already held, so it never holds back the other legs.
export function quickSkillsLeg(
  workspaceId: string,
  query: string,
): Promise<SkillSearchHit[]> | undefined {
  const skills = held?.workspaceId === workspaceId ? held.skills : undefined;
  if (!fresh(workspaceId) || !skills) void workspaceSkills(workspaceId).catch(() => undefined); // intentional: reported inside workspaceSkills
  return skills ? Promise.resolve(workspaceSkillHits(skills, query)) : undefined;
}

export async function searchSkillsLeg(
  workspaceId: string,
  query: string,
): Promise<SkillsLegResult> {
  const own = await workspaceSkills(workspaceId);
  try {
    const found = await client.searchSkillMarketplaces(workspaceId, query);
    return { hits: skillSearchHits(own, query, found.results), marketplaceFailed: false };
  } catch (err) {
    // The query is the person's text: it never goes to telemetry, only that the search failed.
    reportUiFailure("search.skills_marketplace", err, { workspaceId });
    return { hits: workspaceSkillHits(own, query), marketplaceFailed: true };
  }
}

export function openSkillsTarget(
  workspaceId: string,
  target: Parameters<typeof skillsNav.open>[0],
): void {
  skillsNav.open(target);
  agentsPaneState.subTab = "skills";
  const path = window.location.pathname;
  if (path.includes(`/workspace/${workspaceId}/agents`) || path.endsWith("/skills")) return;
  void goto(`/workspace/${workspaceId}/agents`);
}

export function openSkillHit(workspaceId: string, hit: SkillSearchHit): void {
  openSkillsTarget(
    workspaceId,
    hit.kind === "workspace"
      ? { kind: "skill", skillId: hit.skill.id }
      : { kind: "marketplace", result: hit.result },
  );
}

// On Agents → Skills, ⌘F searches skills.
export function onSkillsPlace(pathname: string): boolean {
  return (
    pathname.endsWith("/skills") ||
    (/\/workspace\/[^/]+\/agents\/?$/.test(pathname) && agentsPaneState.subTab === "skills")
  );
}
